import { PetType } from 'constants/pet-type';
import {
  AllergySegmentationIds,
  BreedSegmentationIds,
  DogAgeSegmentationIds,
  NutritionSegmentationIds,
} from 'constants/segmentation';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { DogProfile, PetProfile } from 'types/pet-profile';
import {
  addArrayPropertyIfNotEmpty,
  addPropertyIfValid,
  parseAndValidateCommaSeparatedValues,
  validateSingleValue,
} from './utils';

const VALID_DOG_AGES = Object.values(
  DogAgeSegmentationIds
) as readonly string[];
const VALID_DOG_BREEDS = Object.values(
  BreedSegmentationIds
) as readonly string[];
const VALID_DOG_NEEDS = Object.values(
  NutritionSegmentationIds
) as readonly string[];
const VALID_DOG_ALLERGIES = Object.values(
  AllergySegmentationIds
) as readonly string[];

/**
 * This hook is used to set the pet profile from the query params.
 *
 * IMPORTANT: Requires pp-edit=1 query parameter to activate processing.
 * Without pp-edit=1, all pet profile parameters (age, breed, needs, allergies) are ignored.
 *
 * This safety mechanism prevents accidental application of pet profile data.
 *
 * Example URL: /any-page?pp-edit=1&age=welpe&breed=retriever&needs=digestion
 */
export const useSetPetFromQueryParams = (
  setPetProfile: (profile: PetProfile) => void
): void => {
  const { isReady, query } = useRouter();
  const { age, breed, needs, allergies } = query;
  const ppEdit = query['pp-edit'];

  useEffect(() => {
    if (!isReady) return;

    // If pp-edit is not set to '1', skip the entire logic
    if (ppEdit !== '1') return;

    // Start with a clear dog profile (as per ticket, it's dog only for now)
    let newProfile: DogProfile = {
      petType: PetType.Dog,
      id: undefined,
      selectedAge: undefined,
      selectedNeeds: undefined,
      selectedAllergies: undefined,
      selectedFoodTypes: undefined,
      lastTouched: undefined,
      updatedAt: undefined,
      selectedWeight: undefined,
      name: undefined,
    };

    // Validate and set age
    const validAge = validateSingleValue(age, VALID_DOG_AGES);
    newProfile = addPropertyIfValid(
      newProfile,
      'selectedAge',
      validAge as DogAgeSegmentationIds | undefined
    );

    // Validate and set breed
    const validBreed = validateSingleValue(breed, VALID_DOG_BREEDS);
    newProfile = addPropertyIfValid(
      newProfile,
      'selectedBreed',
      validBreed as BreedSegmentationIds | undefined
    );

    // Validate and set needs (can be comma-separated)
    const validNeeds = parseAndValidateCommaSeparatedValues(
      needs,
      VALID_DOG_NEEDS
    );
    newProfile = addArrayPropertyIfNotEmpty(
      newProfile,
      'selectedNeeds',
      validNeeds as NutritionSegmentationIds[]
    );

    // Validate and set allergies (can be comma-separated)
    // Only set allergies if 'intolerance' is in the needs
    if (validNeeds.includes('intolerance' as NutritionSegmentationIds)) {
      const validAllergies = parseAndValidateCommaSeparatedValues(
        allergies,
        VALID_DOG_ALLERGIES
      );
      newProfile = addArrayPropertyIfNotEmpty(
        newProfile,
        'selectedAllergies',
        validAllergies as AllergySegmentationIds[]
      );
    }

    // Set the profile
    setPetProfile(newProfile);
  }, [isReady, age, breed, needs, allergies, ppEdit, setPetProfile]);
};
